import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import qs from 'qs'

import { useDispatch } from '@tabeeb/store'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { ApiErrorResponse, UserResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import {
  GetUserQualificationSortColumn,
  SortingOrder,
  CreateUserQualificationRequest,
} from '@tabeeb/contracts/microservices/swagger-certificate-service'
import type { UserQualificationFilterModelDto } from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'

import { QualificationsQueryKeys } from '../queries'
import { UserQualificationsDisplayType } from '../constants'
import type { UserQualification } from '../types'
import { Scope } from '../enums'

export interface UseUserQualificationsGridProps {
  search?: string
  scope?: Scope
  sorting?: { field: GetUserQualificationSortColumn; order: SortingOrder } | null
  filtering?: UserQualificationFilterModelDto[]
  pageNumber?: number
  pageSize?: number
  userGuid?: string
  userId?: number
  contractVersionId?: number
  displayType?: typeof UserQualificationsDisplayType[keyof typeof UserQualificationsDisplayType]
}

const useUserQualifications = ({
  search,
  scope = Scope.Tenant,
  sorting,
  filtering = [],
  pageNumber = 0,
  pageSize = 25,
  userGuid,
  userId,
  contractVersionId,
  displayType = UserQualificationsDisplayType.All,
}: UseUserQualificationsGridProps) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { certificateServiceApi: api, mainApi } = useApi()

  const isContractDisplay = displayType === UserQualificationsDisplayType.Contract

  const userQualificationsQuery = useQuery({
    queryKey: [
      QualificationsQueryKeys.UserQualifications,
      isContractDisplay,
      displayType,
      search,
      scope,
      sorting,
      filtering,
      pageNumber,
      pageSize,
      userGuid,
      userId,
      contractVersionId,
    ],
    queryFn: async () => {
      const getUserQualificationsParams = {
        ...(userGuid && userId && { UserGuid: userGuid, UserId: userId }),
        ...(contractVersionId && { ContractVersionId: contractVersionId }),
        ...(sorting && {
          'Sort.Field': sorting.field,
          'Sort.SortOrder': sorting.order,
        }),
        Scope: scope,
        FilterModels: filtering,
        PageNumber: pageNumber,
        PageSize: pageSize,
        ...(search && { Search: search }),
      }

      const qualificationsResponse = isContractDisplay
        ? await api.userQualifications.getForContractAsync(
            {
              ...getUserQualificationsParams,
              ContractVersionId: contractVersionId!,
            },
            {
              paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
            }
          )
        : await api.userQualifications.getAllWithPaginationAsync(getUserQualificationsParams, {
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          })

      const userGuids = new Set<string>()
      for (const userQualification of qualificationsResponse.UserQualifications) {
        userGuids.add(userQualification.AssignedToUserId)
        userGuids.add(userQualification.AssignerId)
      }

      let users: UserResponse[] = []
      if (userGuids.size > 0) {
        const usersResponse = await mainApi.users.getUsersByIdentityGuidsAsync(
          {
            IdentityGuids: Array.from(userGuids),
          },
          {
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          }
        )
        users = usersResponse ?? []
      }

      const qualificationsWithUsers = qualificationsResponse.UserQualifications.map((qualification) => ({
        ...qualification,
        AssignedUser: users.find((u) => u.IdentityGuid === qualification.AssignedToUserId),
        AssignerUser: users.find((u) => u.IdentityGuid === qualification.AssignerId),
      }))

      return {
        UserQualifications: qualificationsWithUsers as UserQualification[],
        TotalCount: qualificationsResponse.TotalCount,
      }
    },
    enabled: isContractDisplay ? !!contractVersionId : true,
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateUserQualificationRequest) => api.userQualifications.createUserQualificationAsync(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.UserQualifications] })
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {},
  })

  const revokeMutation = useMutation({
    mutationFn: (userQualificationId: number) => api.userQualifications.revokeAsync(userQualificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.UserQualifications] })
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    },
  })

  const restoreMutation = useMutation({
    mutationFn: ({
      userQualificationId,
      userGuid: restoreUserGuid,
    }: {
      userQualificationId: number
      userGuid: string
    }) => api.userQualifications.restoreAsync(userQualificationId, restoreUserGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.UserQualifications] })
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    },
  })

  const loading = useMemo(
    () =>
      userQualificationsQuery.isLoading ||
      userQualificationsQuery.isFetching ||
      createMutation.isPending ||
      revokeMutation.isPending ||
      restoreMutation.isPending,
    [
      userQualificationsQuery.isLoading,
      userQualificationsQuery.isFetching,
      createMutation.isPending,
      revokeMutation.isPending,
      restoreMutation.isPending,
    ]
  )

  const onReload = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.UserQualifications] })
  }, [queryClient])

  return useMemo(
    () => ({
      loading,
      qualifications: userQualificationsQuery.data?.UserQualifications ?? [],
      totalCount: userQualificationsQuery.data?.TotalCount ?? 0,
      createAsync: createMutation.mutateAsync,
      revokeAsync: revokeMutation.mutateAsync,
      restoreAsync: restoreMutation.mutateAsync,
      onReload,
    }),
    [
      loading,
      userQualificationsQuery.data,
      onReload,
      createMutation.mutateAsync,
      restoreMutation.mutateAsync,
      revokeMutation.mutateAsync,
    ]
  )
}

export default useUserQualifications
